import React, { useEffect, useState } from 'react';
import type { Address, CreateUnitRequest, UnitResponse, UpdateUnitRequest } from '@troia-v3/types';

import { Drawer, Button, Input, Textarea } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { documentDigitsOnly, formatDocumentInput } from '@/shared/utils/documentMask';
import { useCreateUnit, useUpdateUnit } from '../hooks/useUnits';
import { UnitMembersSection } from './UnitMembersSection';

interface UnitFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unit?: UnitResponse | null;
}

interface AddressFormState {
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

const EMPTY_ADDRESS: AddressFormState = {
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'BR',
};

/**
 * Drawer de criação/edição de Unidades.
 *
 * Molde estrutural: `src/modules/goals/components/GoalFormDrawer.tsx`.
 * A gestão de membros (`UnitMembersSection`, molde `TeamMembersSection`) só
 * é exibida em modo edição — a unidade precisa existir (ter `id`) antes de
 * poder receber membros, mesma decisão do `TeamFormPage`.
 */
export const UnitFormDrawer: React.FC<UnitFormDrawerProps> = ({ isOpen, onClose, unit }) => {
  const isEdit = !!unit;
  const createMut = useCreateUnit();
  const updateMut = useUpdateUnit();

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [unitDocument, setUnitDocument] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState<AddressFormState>(EMPTY_ADDRESS);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    if (unit) {
      setName(unit.name);
      setCode(unit.code ?? '');
      // Persistido só-dígitos; exibe mascarado.
      setUnitDocument(unit.document ? formatDocumentInput(unit.document, 'PJ') : '');
      setDescription(unit.description ?? '');
      setAddress({
        street: unit.address?.street ?? '',
        number: unit.address?.number ?? '',
        complement: unit.address?.complement ?? '',
        neighborhood: unit.address?.neighborhood ?? '',
        city: unit.address?.city ?? '',
        state: unit.address?.state ?? '',
        zipCode: unit.address?.zipCode ?? '',
        country: unit.address?.country ?? 'BR',
      });
      setLatitude(unit.location?.latitude != null ? String(unit.location.latitude) : '');
      setLongitude(unit.location?.longitude != null ? String(unit.location.longitude) : '');
    } else {
      setName('');
      setCode('');
      setUnitDocument('');
      setDescription('');
      setAddress(EMPTY_ADDRESS);
      setLatitude('');
      setLongitude('');
    }
    setFormError(null);
  }, [isOpen, unit]);

  const handleAddressChange = <K extends keyof AddressFormState>(field: K, value: AddressFormState[K]) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const isSubmitting = createMut.isPending || updateMut.isPending;

  /**
   * Endereço é opcional — mas se qualquer campo foi preenchido, os
   * obrigatórios do shape `Address` (@troia-v3/types) precisam estar
   * completos. Molde: `CustomerFormPage.tsx` (`buildAddressPayload`).
   */
  const buildAddressPayload = (): Address | undefined => {
    const hasAnyField = [
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state,
      address.zipCode,
    ].some((v) => v.trim().length > 0);

    if (!hasAnyField) return undefined;

    return {
      street: address.street.trim(),
      number: address.number.trim(),
      ...(address.complement.trim() ? { complement: address.complement.trim() } : {}),
      neighborhood: address.neighborhood.trim(),
      city: address.city.trim(),
      state: address.state.trim().toUpperCase(),
      zipCode: address.zipCode.trim(),
      country: address.country.trim() || 'BR',
    };
  };

  const validateAddress = (): string | null => {
    const hasAnyField = [
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state,
      address.zipCode,
    ].some((v) => v.trim().length > 0);
    if (!hasAnyField) return null;

    if (!address.street.trim()) return 'Endereço: rua é obrigatória quando o endereço é preenchido';
    if (!address.number.trim()) return 'Endereço: número é obrigatório quando o endereço é preenchido';
    if (!address.neighborhood.trim()) return 'Endereço: bairro é obrigatório quando o endereço é preenchido';
    if (!address.city.trim()) return 'Endereço: cidade é obrigatória quando o endereço é preenchido';
    if (address.state.trim().length !== 2) return 'Endereço: UF deve ter 2 letras (ex: SP)';
    if (!address.zipCode.trim()) return 'Endereço: CEP é obrigatório quando o endereço é preenchido';
    return null;
  };

  const validateLocation = (): string | null => {
    const hasLat = latitude.trim().length > 0;
    const hasLng = longitude.trim().length > 0;
    if (!hasLat && !hasLng) return null;
    if (hasLat !== hasLng) return 'Localização: preencha latitude e longitude juntas (ou deixe as duas vazias)';
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) return 'Latitude inválida (entre -90 e 90)';
    if (!Number.isFinite(lng) || lng < -180 || lng > 180) return 'Longitude inválida (entre -180 e 180)';
    return null;
  };

  const handleSubmit = async (): Promise<void> => {
    setFormError(null);

    if (!name.trim()) {
      setFormError('Nome é obrigatório');
      return;
    }

    const addressError = validateAddress();
    if (addressError) {
      setFormError(addressError);
      return;
    }

    const locationError = validateLocation();
    if (locationError) {
      setFormError(locationError);
      return;
    }

    // Só tamanho, sem dígito verificador — mesma régua do cadastro de clientes.
    // Validar mod-11 aqui passaria a rejeitar documento que já está gravado.
    const documentDigits = documentDigitsOnly(unitDocument);
    if (documentDigits.length > 0 && documentDigits.length !== 14) {
      setFormError('CNPJ deve ter 14 dígitos');
      return;
    }

    const addressPayload = buildAddressPayload();
    const hasLocation = latitude.trim().length > 0 && longitude.trim().length > 0;
    const locationPayload = hasLocation
      ? { latitude: Number(latitude), longitude: Number(longitude) }
      : undefined;

    const trimmedCode = code.trim();
    const trimmedDescription = description.trim();

    if (isEdit && unit) {
      // Campo esvaziado vai como `null` = LIMPAR. Omitir a chave significa
      // "não mexer" pro backend, então o payload antigo (só com o que tinha
      // conteúdo) tornava impossível apagar Código/Descrição/Endereço/Local:
      // salvava "com sucesso" e o valor antigo voltava ao reabrir o drawer.
      const payload: UpdateUnitRequest = {
        name: name.trim(),
        code: trimmedCode || null,
        document: documentDigits || null,
        description: trimmedDescription || null,
        address: addressPayload ?? null,
        location: locationPayload ?? null,
      };
      await updateMut.mutateAsync({ id: unit.id, data: payload });
    } else {
      const payload: CreateUnitRequest = {
        name: name.trim(),
        ...(trimmedCode ? { code: trimmedCode } : {}),
        ...(documentDigits ? { document: documentDigits } : {}),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(addressPayload ? { address: addressPayload } : {}),
        ...(locationPayload ? { location: locationPayload } : {}),
      };
      await createMut.mutateAsync(payload);
    }
    onClose();
  };

  const footer = (
    <>
      <Button
        variant="secondary"
        onClick={onClose}
        disabled={isSubmitting}
        data-testid="unit-form-cancel"
      >
        Cancelar
      </Button>
      <Button
        onClick={handleSubmit}
        loading={isSubmitting}
        disabled={isSubmitting}
        data-testid="unit-form-submit"
      >
        {isEdit ? 'Salvar' : 'Criar'}
      </Button>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Editar unidade' : 'Nova unidade'}
      footer={footer}
    >
      <div className="flex flex-col gap-5 px-6 py-5" data-testid="unit-form-drawer">
        {formError && (
          <div
            className="rounded-lg px-3 py-2 text-sm"
            style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }}
            data-testid="unit-form-error"
          >
            {formError}
          </div>
        )}

        {/* Informações básicas */}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Nome *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Loja Centro, Filial Norte"
                data-testid="unit-form-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Código
              </label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Ex: LJ-01"
                data-testid="unit-form-code"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                CNPJ
              </label>
              <Input
                value={unitDocument}
                onChange={(e) => setUnitDocument(formatDocumentInput(e.target.value, 'PJ'))}
                placeholder="00.000.000/0000-00"
                inputMode="numeric"
                data-testid="unit-form-document"
              />
              <p className="mt-1 text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Usado para casar a unidade com fontes externas de dados, como contadores de visitantes.
              </p>
            </div>
          </div>

          <Textarea
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Observações sobre a unidade (opcional)"
            data-testid="unit-form-description"
          />
        </div>

        {/* Endereço */}
        <div className="flex flex-col gap-3 pt-1">
          <div className="flex items-center gap-2">
            <Icon name="map-pin" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
              Endereço
            </h3>
          </div>
          <p className="text-xs -mt-2" style={{ color: 'var(--theme-muted-text)' }}>
            Opcional — se iniciar, preencha os campos obrigatórios juntos (rua, número, bairro, cidade, CEP e UF)
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Rua
              </label>
              <Input
                value={address.street}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder="Av. Paulista"
                data-testid="unit-form-address-street"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Número
              </label>
              <Input
                value={address.number}
                onChange={(e) => handleAddressChange('number', e.target.value)}
                placeholder="1000"
                data-testid="unit-form-address-number"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Complemento
              </label>
              <Input
                value={address.complement}
                onChange={(e) => handleAddressChange('complement', e.target.value)}
                placeholder="Sala 42"
                data-testid="unit-form-address-complement"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Bairro
              </label>
              <Input
                value={address.neighborhood}
                onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                placeholder="Bela Vista"
                data-testid="unit-form-address-neighborhood"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[2fr_80px_1fr] gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Cidade
              </label>
              <Input
                value={address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder="São Paulo"
                data-testid="unit-form-address-city"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                UF
              </label>
              <Input
                value={address.state}
                onChange={(e) => handleAddressChange('state', e.target.value.toUpperCase().slice(0, 2))}
                placeholder="SP"
                maxLength={2}
                data-testid="unit-form-address-state"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                CEP
              </label>
              <Input
                value={address.zipCode}
                onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                placeholder="01310-100"
                data-testid="unit-form-address-zipcode"
              />
            </div>
          </div>
        </div>

        {/* Localização (geo-check) */}
        <div className="flex flex-col gap-3 pt-1">
          <div className="flex items-center gap-2">
            <Icon name="target" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
            <h3 className="text-sm font-semibold" style={{ color: 'var(--theme-text)' }}>
              Localização
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Latitude
              </label>
              <Input
                type="number"
                step="any"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-23.561399"
                data-testid="unit-form-latitude"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--theme-text)' }}>
                Longitude
              </label>
              <Input
                type="number"
                step="any"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-46.655880"
                data-testid="unit-form-longitude"
              />
            </div>
          </div>
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Opcional — usado no geo-check das auditorias (checklists preenchidos longe da unidade são sinalizados)
          </p>
        </div>

        {/* Equipe da unidade — só após a unidade existir */}
        {isEdit && unit ? (
          <UnitMembersSection unitId={unit.id} />
        ) : (
          <div className="flex items-center gap-2 px-1">
            <Icon name="info" className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
            <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
              O gerente e os membros são adicionados após a criação da unidade.
            </p>
          </div>
        )}
      </div>
    </Drawer>
  );
};

UnitFormDrawer.displayName = 'UnitFormDrawer';
